import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { getAds, createAd, updateAd } from "../../services/adsService";

const toastCfg = { autoClose: 1800 };

/* Predefined common ad sizes — admin can also type custom values */
const PRESET_SIZES = [
  { label: "1024 × 150 (Home/Betting Large)", width: 1024, height: 150 },
  { label: "1024 × 100 (Home/Betting Small)",  width: 1024, height: 100 },
  { label: "728 × 90 (Leaderboard)",            width: 728,  height: 90  },
  { label: "300 × 250 (Medium Rectangle)",      width: 300,  height: 250 },
  { label: "320 × 50 (Mobile Banner)",          width: 320,  height: 50  },
  { label: "Custom",                             width: "",   height: ""  },
];

const BASE = "https://opiniongames.net/";

const s = {
  root:        { padding: "20px 24px 40px", background: "#f1f5f9", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },
  headerWrap:  { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 },
  pageTitle:   { margin: 0, fontWeight: 800, fontSize: "1.5rem", color: "#0f172a" },
  pageSub:     { margin: "4px 0 0", color: "#64748b", fontSize: "0.85rem" },
  grid:        { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, alignItems: "start" },
  card:        { background: "#fff", borderRadius: 16, border: "1px solid #e8edf2", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", overflow: "hidden" },
  cardHeader:  { padding: "14px 20px", borderBottom: "1px solid #f1f5f9", background: "#f8fafc", fontWeight: 800, color: "#0f172a", fontSize: "0.95rem", display: "flex", alignItems: "center", gap: 8 },
  cardBody:    { padding: "20px" },
  label:       { display: "block", fontSize: "0.72rem", fontWeight: 700, color: "#475569", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.04em" },
  input:       { width: "100%", padding: "10px 14px", border: "1.5px solid #e2e8f0", borderRadius: 9, fontSize: "0.87rem", color: "#1e293b", background: "#f8fafc", outline: "none", fontFamily: "inherit", marginBottom: 16, boxSizing: "border-box" },
  hint:        { fontSize: "0.72rem", color: "#94a3b8", marginTop: -12, marginBottom: 14 },
  row2:        { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  saveBtn:     { display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "12px 24px", background: "linear-gradient(135deg,#16632b,#1da144)", border: "none", borderRadius: 9, fontFamily: "inherit", fontSize: "0.87rem", fontWeight: 700, color: "#fff", cursor: "pointer", width: "100%", marginTop: 4 },
  saveBtnDis:  { opacity: 0.65, cursor: "not-allowed" },
  backBtn:     { display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 18px", border: "1.5px solid #e2e8f0", borderRadius: 9, background: "#fff", fontFamily: "inherit", fontSize: "0.84rem", fontWeight: 600, color: "#475569", cursor: "pointer", marginBottom: 20, textDecoration: "none" },
  previewBox:  { background: "#f8fafc", borderRadius: 10, border: "1px dashed #cbd5e1", padding: 10, minHeight: 80, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" },
  previewImg:  { maxWidth: "100%", maxHeight: 220, objectFit: "contain", borderRadius: 6 },
  previewEmpty:{ color: "#94a3b8", fontSize: "0.85rem", fontWeight: 600, textAlign: "center" },
  sizeBox:     { background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 9, padding: "10px 14px", fontSize: "0.8rem", color: "#166534", fontWeight: 700, marginBottom: 16 },
};

const emptyForm = {
  title:              "",
  page:               "home",
  preset:             "",
  width:              1024,
  height:             150,
  click_url:          "",
  display_order:      0,
  slideshow_duration: 5,
  is_active:          1,
};

export const AddAdsLayer = () => {
  const { id }        = useParams();
  const navigate      = useNavigate();
  const isEdit        = Boolean(id);
  const fileRef       = useRef();

  const [form, setForm]           = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview]     = useState("");
  const [saving, setSaving]       = useState(false);

  /* Load existing ad when editing */
  useEffect(() => {
    if (!isEdit) return;
    getAds()
      .then((res) => {
        const ad = (res?.data ?? []).find((a) => a.id === parseInt(id));
        if (ad) {
          setForm({
            title:              ad.title,
            page:               ad.page,
            preset:             "",
            width:              ad.width,
            height:             ad.height,
            click_url:          ad.click_url || "",
            display_order:      ad.display_order,
            slideshow_duration: ad.slideshow_duration,
            is_active:          ad.is_active,
          });
          setPreview(ad.image_url || "");
        }
      })
      .catch(() => toast.error("Failed to load ad", toastCfg));
  }, [id, isEdit]);

  /* Apply preset size */
  const handlePreset = (e) => {
    const idx = parseInt(e.target.value);
    if (isNaN(idx)) return;
    const p = PRESET_SIZES[idx];
    setForm((f) => ({ ...f, preset: idx, width: p.width || f.width, height: p.height || f.height }));
  };

  /* Preview image on file pick */
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!imageFile && !isEdit) {
      toast.warn("Please upload an ad image", toastCfg);
      return;
    }
    if (!form.title.trim()) {
      toast.warn("Title is required", toastCfg);
      return;
    }
    if (parseInt(form.width) <= 0 || parseInt(form.height) <= 0) {
      toast.warn("Width and height must be positive numbers", toastCfg);
      return;
    }

    const fd = new FormData();
    if (isEdit) fd.append("id", id);
    fd.append("title",              form.title.trim());
    fd.append("page",               form.page);
    fd.append("width",              form.width);
    fd.append("height",             form.height);
    fd.append("click_url",          form.click_url);
    fd.append("display_order",      form.display_order);
    fd.append("slideshow_duration", form.slideshow_duration);
    fd.append("is_active",          form.is_active);
    if (imageFile) fd.append("image_file", imageFile);

    setSaving(true);
    const call = isEdit ? updateAd(fd) : createAd(fd);
    call
      .then((res) => {
        if (res?.status) {
          toast.success(isEdit ? "Ad updated successfully" : "Ad created successfully", toastCfg);
          setTimeout(() => navigate("/ads-list"), 700);
        } else {
          toast.error(res?.msg || "Save failed", toastCfg);
        }
      })
      .catch(() => toast.error("Save failed", toastCfg))
      .finally(() => setSaving(false));
  };

  return (
    <div style={s.root}>
      {/* ── Header ── */}
      <div style={s.headerWrap}>
        <div>
          <h4 style={s.pageTitle}>{isEdit ? "Edit Advertisement" : "Add New Advertisement"}</h4>
          <p style={s.pageSub}>
            {isEdit ? "Update ad details and image" : "Create a new banner ad with size and slideshow settings"}
          </p>
        </div>
        <button style={s.backBtn} onClick={() => navigate("/ads-list")}>
          <Icon icon="solar:arrow-left-bold" />
          Back to List
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={s.grid}>
          {/* ── Left: Form ── */}
          <div style={s.card}>
            <div style={s.cardHeader}>
              <Icon icon="solar:pen-bold-duotone" style={{ color: "#3b82f6", fontSize: 18 }} />
              Ad Details
            </div>
            <div style={s.cardBody}>

              {/* Title */}
              <label style={s.label}>Ad Title *</label>
              <input
                style={s.input}
                name="title"
                placeholder="e.g. Home Page Large Banner"
                value={form.title}
                onChange={handleChange}
                required
              />

              {/* Page */}
              <label style={s.label}>Target Page *</label>
              <select style={s.input} name="page" value={form.page} onChange={handleChange}>
                <option value="home">Home</option>
                <option value="betting">Betting</option>
                <option value="match">Match</option>
              </select>

              {/* Preset size picker */}
              <label style={s.label}>Size Preset</label>
              <select style={s.input} value={form.preset} onChange={handlePreset}>
                <option value="">— Select a preset or enter custom —</option>
                {PRESET_SIZES.map((p, i) => (
                  <option key={i} value={i}>{p.label}</option>
                ))}
              </select>

              {/* Width / Height */}
              <div style={s.row2}>
                <div>
                  <label style={s.label}>Width (px) *</label>
                  <input style={s.input} name="width" type="number" min="1" value={form.width} onChange={handleChange} required />
                </div>
                <div>
                  <label style={s.label}>Height (px) *</label>
                  <input style={s.input} name="height" type="number" min="1" value={form.height} onChange={handleChange} required />
                </div>
              </div>
              {form.width && form.height && (
                <div style={s.sizeBox}>
                  <Icon icon="solar:ruler-bold-duotone" style={{ marginRight: 6 }} />
                  Ad size: {form.width} × {form.height} px
                </div>
              )}

              {/* Slideshow duration */}
              <label style={s.label}>Slideshow Duration (seconds)</label>
              <input
                style={s.input}
                name="slideshow_duration"
                type="number"
                min="1"
                max="60"
                value={form.slideshow_duration}
                onChange={handleChange}
              />
              <p style={s.hint}>Time each ad slide is shown before auto-advancing</p>

              {/* Display order */}
              <label style={s.label}>Display Order</label>
              <input
                style={s.input}
                name="display_order"
                type="number"
                min="0"
                value={form.display_order}
                onChange={handleChange}
              />
              <p style={s.hint}>Lower number = shown first in the slideshow</p>

              {/* Click URL */}
              <label style={s.label}>Click URL (optional)</label>
              <input
                style={s.input}
                name="click_url"
                type="url"
                placeholder="https://example.com"
                value={form.click_url}
                onChange={handleChange}
              />

              {/* Status */}
              <label style={s.label}>Status</label>
              <select style={s.input} name="is_active" value={form.is_active} onChange={handleChange}>
                <option value={1}>Active (Visible)</option>
                <option value={0}>Inactive (Hidden)</option>
              </select>

              {/* Image upload */}
              <label style={s.label}>Ad Image {!isEdit && "*"}</label>
              <input
                ref={fileRef}
                style={s.input}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleFile}
              />
              {isEdit && <p style={s.hint}>Leave empty to keep the existing image</p>}

              {/* Submit */}
              <button type="submit" style={{ ...s.saveBtn, ...(saving ? s.saveBtnDis : {}) }} disabled={saving}>
                {saving ? (
                  <span className="spinner-border spinner-border-sm" />
                ) : (
                  <>
                    <Icon icon="solar:server-square-bold" style={{ fontSize: 16 }} />
                    {isEdit ? "Update Ad" : "Create Ad"}
                  </>
                )}
              </button>
            </div>
          </div>

          {/* ── Right: Preview ── */}
          <div style={s.card}>
            <div style={s.cardHeader}>
              <Icon icon="solar:eye-bold-duotone" style={{ color: "#1da144", fontSize: 18 }} />
              Image Preview
            </div>
            <div style={s.cardBody}>
              <div style={s.previewBox}>
                {preview ? (
                  <img src={preview} alt="ad preview" style={s.previewImg} />
                ) : (
                  <div style={s.previewEmpty}>
                    <Icon icon="solar:gallery-bold-duotone" style={{ fontSize: 36, display: "block", margin: "0 auto 10px", color: "#cbd5e1" }} />
                    No image selected
                  </div>
                )}
              </div>

              {/* Size info card */}
              <div style={{ marginTop: 20, background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
                <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: 10 }}>
                  Ad Configuration Summary
                </div>
                {[
                  ["Page",              { home: "Home", betting: "Betting", match: "Match" }[form.page] || form.page],
                  ["Size",              `${form.width} × ${form.height} px`],
                  ["Slideshow",         `${form.slideshow_duration}s per slide`],
                  ["Display Order",     form.display_order],
                  ["Status",            form.is_active == 1 ? "Active" : "Inactive"],
                  ["Click URL",         form.click_url || "—"],
                ].map(([label, val]) => (
                  <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #f1f5f9", fontSize: "0.83rem" }}>
                    <span style={{ color: "#64748b", fontWeight: 600 }}>{label}</span>
                    <span style={{ color: "#0f172a", fontWeight: 700, textAlign: "right", maxWidth: "60%", wordBreak: "break-all" }}>{String(val)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
